import React, { useCallback, useEffect, useState } from 'react';
import { KeyRound, Plus, Trash2 } from 'lucide-react';
import {
  getApiErrorMessage,
  orchestrationKeysService,
  type OrchestrationKeysDto,
} from '../../../../components/api/orchestrationKeysService';

const inputClass =
  'w-full bg-neutral-800 border border-neutral-700 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed';

const OrchestrationKeysCard: React.FC = () => {
  const [apiKey, setApiKey] = useState('');
  const [secret, setSecret] = useState('');
  const [current, setCurrent] = useState<OrchestrationKeysDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCurrent(await orchestrationKeysService.get());
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load orchestration API keys.'));
      setCurrent(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const canSave = Boolean(apiKey.trim() && secret.trim()) && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = await orchestrationKeysService.upsert(apiKey.trim(), secret.trim());
      setCurrent(saved);
      setApiKey('');
      setSecret('');
      setSuccess('Orchestration API keys saved.');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save orchestration API keys.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleting || !current?.configured) return;
    if (!window.confirm('Delete this tenant orchestration API key? Published workflow tools will stop until a new key is saved.')) {
      return;
    }
    setDeleting(true);
    setError(null);
    setSuccess(null);
    try {
      await orchestrationKeysService.remove();
      setCurrent({ configured: false, apiKeyMasked: '', hasSecret: false });
      setSuccess('Orchestration API keys deleted.');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to delete orchestration API keys.'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-5">
      <div className="flex items-center gap-2 mb-4 text-gray-400">
        <KeyRound className="w-4 h-4" />
        <h3 className="font-medium text-white text-base">Orchestration API Keys</h3>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Tenant-wide Studio key and secret sent as <span className="font-mono">X-Api-Key</span> and{' '}
        <span className="font-mono">X-Api-Secret</span>. CoPilot keys are unchanged.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-gray-400 text-xs">API key</span>
          <input
            type="password"
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
            placeholder="ak_live_…"
            className={inputClass}
            aria-label="Orchestration API key"
            autoComplete="off"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-gray-400 text-xs">API secret</span>
          <input
            type="password"
            value={secret}
            onChange={(event) => setSecret(event.target.value)}
            placeholder="sk_live_…"
            className={inputClass}
            aria-label="Orchestration API secret"
            autoComplete="off"
          />
        </label>
      </div>
      {error ? <p className="text-xs text-rose-400 mb-3">{error}</p> : null}
      {success ? <p className="text-xs text-emerald-400 mb-3">{success}</p> : null}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={!canSave}
          className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm transition-colors"
        >
          <Plus className="w-4 h-4" />
          {saving ? 'Saving…' : current?.configured ? 'Replace key' : 'Save key'}
        </button>
        {current?.configured ? (
          <button
            type="button"
            onClick={() => void handleDelete()}
            disabled={deleting}
            className="inline-flex items-center gap-2 text-rose-400 hover:text-rose-300 disabled:opacity-50 text-sm"
          >
            <Trash2 className="w-4 h-4" />
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        ) : null}
      </div>
      {loading ? (
        <p className="mt-5 text-xs text-gray-400">Loading orchestration keys…</p>
      ) : current?.configured ? (
        <p className="mt-5 text-xs text-gray-400 font-mono">
          Saved key {current.apiKeyMasked || '••••'} · secret {current.hasSecret ? '••••' : '—'}
        </p>
      ) : (
        <p className="mt-5 text-xs text-gray-500">No orchestration API keys configured yet.</p>
      )}
    </div>
  );
};

export default OrchestrationKeysCard;
